import './c100-fonts'
import {
  Box, Container, InputAdornment, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography, useTheme
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { useEffect, useMemo, useState } from 'react'
import PageProps from '../_types/page-props.interface'
import useDocumentTitle from '../hooks/useDocumentTitle'
import { useError } from '../context/error'
import { StyleHelper } from '../components/style-helper'
import LoadingTable from '../components/loading/table.loading'
import { Carnivore100Service } from '../services/carnivore100.service'
import { C100Payload, C100Row, C100Timeframe } from '../_types/carnivore100.type'
import {
  filterRows, sortRows, uniqueMarkets, uniqueSectors, SortKey, SortDir
} from './carnivore100/carnivore100.util'
import TimeframeSelector from '../components/carnivore100/timeframe-selector'
import C100BannerView from '../components/carnivore100/banner'
import FilterChips from '../components/carnivore100/filter-chips'
import { LIST_NAME } from '../components/carnivore100/list-name'
import { COLUMNS, DESC_FIRST } from '../components/carnivore100/columns'
import {
  clearWidth, reorder, setWidth, toggleHidden, useTablePrefs, widthOf
} from '../components/carnivore100/use-table-prefs'
import HeaderCell from '../components/carnivore100/header-cell'
import ColumnPicker from '../components/carnivore100/column-picker'
import { CELL_RENDERERS } from '../components/carnivore100/row-cells'
import { c100Tokens, scrollbarSx } from '../components/carnivore100/c100-tokens'
import { useTickerDrawer } from '../context/ticker-drawer-context'

const MONO = "'IBM Plex Mono', monospace"

// The column-picker column, pinned to the table's right edge.
const PICKER_W = 46

const rowFadeKeyframes = `
@keyframes c100RowFadeIn {
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
}
`

export default function Carnivore100Page(props: PageProps) {
  useDocumentTitle(props.title)
  const { setError } = useError()
  const service = useMemo(() => new Carnivore100Service(setError), [setError])
  const theme = useTheme()
  const t = c100Tokens(theme)
  const { isOpen, setTicker, toggleDrawer } = useTickerDrawer()

  const openTicker = (ticker: string) => {
    setTicker(ticker)
    if (!isOpen) toggleDrawer()
  }

  const [timeframe, setTimeframe] = useState<C100Timeframe>('1D')
  const [payload, setPayload] = useState<C100Payload | null>(null)
  const [loading, setLoading] = useState(true)
  const [errored, setErrored] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  const [search, setSearch] = useState('')
  const [sector, setSector] = useState('All')
  const [market, setMarket] = useState('All')
  const [sortKey, setSortKey] = useState<SortKey>('rank')
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  useEffect(() => {
    let active = true
    ;(async () => {
      setLoading(true)
      setErrored(false)
      const res = await service.getTable(timeframe)
      if (!active) return
      if (res && !(res as any).errMsg) setPayload(res as C100Payload)
      else setErrored(true)
      setLoading(false)
    })()
    return () => {
      active = false
    }
  }, [service, timeframe, reloadKey])

  const rows: C100Row[] = payload?.rows ?? []
  const sectors = useMemo(() => uniqueSectors(rows), [rows])
  const markets = useMemo(() => uniqueMarkets(rows), [rows])
  const visible = useMemo(
    () => sortRows(filterRows(rows, { search, sector, market }), sortKey, sortDir),
    [rows, search, sector, market, sortKey, sortDir]
  )

  const onSort = (key: SortKey) => {
    // Third click on the active column returns to rank order — the published
    // ranking is the default view, and it is always one click away.
    if (key === sortKey) {
      if (sortDir === 'desc' && key !== 'rank') {
        setSortKey('rank')
        setSortDir('asc')
      } else setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
      return
    }
    setSortKey(key)
    setSortDir(DESC_FIRST.has(key) ? 'desc' : 'asc')
  }

  // Pinned cells sit above scrolling content and need an opaque background of
  // their own, including while the row is hovered.
  const { prefs, setPrefs, reset, visibleColumns, isDirty } = useTablePrefs(COLUMNS)
  const [dragId, setDragId] = useState<string | null>(null)
  const [dropId, setDropId] = useState<string | null>(null)

  // Pin offsets are exact now: the table is fixed-layout, so a column's width
  // is what we say it is rather than whatever its content forces.
  const pinLefts = useMemo(() => {
    const out: Record<string, number> = {}
    let x = 0
    for (const col of visibleColumns) {
      if (!col.locked) break
      out[col.id] = x
      x += widthOf(prefs, col)
    }
    return out
  }, [visibleColumns, prefs])

  const totalWidth = useMemo(
    () => visibleColumns.reduce((sum, c) => sum + widthOf(prefs, c), 0) + PICKER_W,
    [visibleColumns, prefs]
  )

  const commitDrop = () => {
    if (dragId && dropId) setPrefs(p => reorder(p, dragId, dropId))
    setDragId(null)
    setDropId(null)
  }

  return (
    <Box sx={{ bgcolor: t.page, minHeight: '100vh' }}>
      {/* inject keyframes */}
      <style>{rowFadeKeyframes}</style>

      <Container sx={{ display: 'flex', flexDirection: 'column', pb: 4, pt: 3 }}>
        {/* Page header */}
        <Box sx={{ mb: 2.5 }}>
          <Typography
            sx={{
              fontFamily: MONO,
              fontSize: 22,
              fontWeight: 600,
              letterSpacing: '0.08em',
              color: t.text,
              textTransform: 'uppercase',
              lineHeight: 1.1
            }}
          >
            {LIST_NAME}
          </Typography>
          <Typography
            sx={{
              fontFamily: MONO,
              fontSize: 10,
              letterSpacing: '0.12em',
              color: t.muted,
              textTransform: 'uppercase',
              mt: 0.5
            }}
          >
            {`WATCHLIST${payload?.version ? ` · v${payload.version}` : ''}`}
          </Typography>
        </Box>

        <TimeframeSelector value={timeframe} onChange={setTimeframe} />

        {loading && <LoadingTable rowCount={8} columnCount={10} />}

        {!loading && errored && (
          <Box
            sx={{
              bgcolor: t.card,
              border: `1px solid ${t.hairline}`,
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              mt: 2
            }}
          >
            <Typography sx={{ color: t.text, fontFamily: MONO, fontSize: 13 }}>
              Couldn't load the list.
            </Typography>
            <Typography sx={{ color: t.muted, fontFamily: MONO, fontSize: 11, mt: 0.75 }}>
              The market data service didn't respond in time.
            </Typography>
            <Box
              component="button"
              onClick={() => setReloadKey(k => k + 1)}
              sx={{
                mt: 2.5,
                cursor: 'pointer',
                fontFamily: MONO,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: t.accent,
                bgcolor: t.faintAccentBg,
                border: `1px solid ${t.accent}`,
                borderRadius: 999,
                px: 2.5,
                py: 0.75,
                transition: 'background-color .15s',
                '&:hover': { bgcolor: t.accent, color: '#fff' }
              }}
            >
              Retry
            </Box>
          </Box>
        )}

        {!loading && !errored && payload && (
          <>
            <C100BannerView banner={payload.banner} timeframe={timeframe} />

            {/* Control bar */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1.5,
                alignItems: { xs: 'stretch', sm: 'flex-start' },
                mb: 1.5
              }}
            >
              {/* Search */}
              <TextField
                size="small"
                placeholder="Search ticker or name…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 16, color: t.muted }} />
                    </InputAdornment>
                  )
                }}
                sx={{
                  width: { xs: '100%', sm: 280 },
                  flexShrink: 0,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: t.elevated,
                    borderRadius: 999,
                    fontFamily: MONO,
                    fontSize: 12,
                    '& fieldset': { borderColor: t.hairline },
                    '&:hover fieldset': { borderColor: t.muted },
                    '&.Mui-focused fieldset': { borderColor: t.accent, borderWidth: 1 }
                  },
                  '& input': {
                    color: t.text,
                    '&::placeholder': { color: t.muted, opacity: 1 }
                  }
                }}
              />

              {/* Filters */}
              <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                <FilterChips label="Sector" options={sectors} value={sector} onChange={setSector} withDots />
                <FilterChips label="Market" options={markets} value={market} onChange={setMarket} />
              </Box>
            </Box>

            {rows.length === 0 ? (
              <Box
                sx={{
                  bgcolor: t.card,
                  border: `1px solid ${t.hairline}`,
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  mt: 2
                }}
              >
                <Typography sx={{ color: t.muted, fontFamily: MONO, fontSize: 13 }}>
                  The list hasn't been published yet. Check back soon.
                </Typography>
              </Box>
            ) : (
              <TableContainer
                component={Box}
                sx={{
                  bgcolor: t.card,
                  border: `1px solid ${t.hairline}`,
                  borderRadius: 2,
                  maxHeight: 800,
                  overflowX: 'scroll',
                  overflowY: 'auto',
                  mt: 1,
                  ...scrollbarSx(t, { persistent: true })
                }}
              >
                <Table
                  stickyHeader
                  aria-label={`${LIST_NAME} table`}
                  size="small"
                  sx={{ tableLayout: 'fixed', width: totalWidth, minWidth: totalWidth }}
                >
                  <TableHead>
                    <TableRow
                      onDrop={e => {
                        e.preventDefault()
                        commitDrop()
                      }}
                      onDragEnd={commitDrop}
                      onDragOver={e => e.preventDefault()}
                      sx={{
                        '& th': {
                          bgcolor: t.card,
                          borderBottom: `1px solid ${t.hairline}`,
                          position: 'sticky',
                          top: 0,
                          zIndex: 20
                        }
                      }}
                    >
                      {visibleColumns.map(col => (
                        <HeaderCell
                          key={col.id}
                          col={col}
                          width={widthOf(prefs, col)}
                          pinLeft={pinLefts[col.id]}
                          active={col.key !== null && sortKey === col.key}
                          sortDir={sortDir}
                          onSort={onSort}
                          onResize={(id, w) => setPrefs(p => setWidth(p, id, w))}
                          onResetWidth={id => setPrefs(p => clearWidth(p, id))}
                          onDragStart={setDragId}
                          onDropOn={setDropId}
                          dropSide={dropId === col.id && dragId !== col.id ? 'left' : null}
                        />
                      ))}
                      <TableCell
                        key="__picker"
                        sx={{
                          p: 0,
                          '&&': {
                            boxSizing: 'border-box',
                            width: PICKER_W,
                            minWidth: PICKER_W,
                            maxWidth: PICKER_W,
                            position: 'sticky',
                            right: 0,
                            zIndex: 31,
                            bgcolor: t.card,
                            borderLeft: `1px solid ${t.hairline}`,
                            textAlign: 'center'
                          }
                        }}
                      >
                        <ColumnPicker
                          columns={COLUMNS}
                          prefs={prefs}
                          onToggle={id => setPrefs(p => toggleHidden(p, id))}
                          onReset={reset}
                          isDirty={isDirty}
                        />
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visible.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={visibleColumns.length + 1}
                          sx={{
                            textAlign: 'center',
                            py: 4,
                            color: t.muted,
                            fontFamily: MONO,
                            fontSize: 12,
                            border: 'none'
                          }}
                        >
                          No tickers match your search or filters.
                        </TableCell>
                      </TableRow>
                    ) : (
                      visible.map((r, index) => {
                        const delay = Math.min(index * 12, 300)
                        return (
                          <TableRow
                            key={r.ticker}
                            onClick={() => openTicker(r.ticker)}
                            sx={{
                              height: 56,
                              cursor: 'pointer',
                              bgcolor: t.card,
                              borderBottom: `1px solid ${t.hairline}`,
                              '&:last-child td': { borderBottom: 'none' },
                              transition: 'background-color .15s, box-shadow .15s',
                              '&:hover': {
                                bgcolor: t.faintAccentBg,
                                boxShadow: `inset 3px 0 0 ${t.accent}`,
                                // the hover tint is translucent, so pinned cells
                                // layer it over their opaque base rather than
                                // inheriting it and going see-through
                                '& .c33-pinned': {
                                  backgroundImage: `linear-gradient(${t.faintAccentBg}, ${t.faintAccentBg})`
                                }
                              },
                              animation: `c100RowFadeIn 0.3s ease both`,
                              animationDelay: `${delay}ms`
                            }}
                          >
                            {visibleColumns.map(col => {
                              const pinned = pinLefts[col.id] !== undefined
                              return (
                                <TableCell
                                  key={col.id}
                                  className={pinned ? 'c33-pinned' : undefined}
                                  sx={{
                                    border: 'none',
                                    py: 1,
                                    overflow: 'hidden',
                                    '&&': {
                                      boxSizing: 'border-box',
                                      width: widthOf(prefs, col),
                                      minWidth: widthOf(prefs, col),
                                      maxWidth: widthOf(prefs, col),
                                      ...(pinned
                                        ? {
                                            position: 'sticky',
                                            left: pinLefts[col.id],
                                            zIndex: 5,
                                            bgcolor: t.card,
                                            whiteSpace: 'nowrap'
                                          }
                                        : {})
                                    }
                                  }}
                                >
                                  {CELL_RENDERERS[col.id]?.(r, t)}
                                </TableCell>
                              )
                            })}
                            {/* keeps the picker column's width reserved in every row */}
                            <TableCell
                              key="__picker"
                              className="c33-pinned"
                              sx={{
                                border: 'none',
                                p: 0,
                                '&&': {
                                  boxSizing: 'border-box',
                                  width: PICKER_W,
                                  minWidth: PICKER_W,
                                  maxWidth: PICKER_W,
                                  position: 'sticky',
                                  right: 0,
                                  zIndex: 5,
                                  bgcolor: t.card,
                                  borderLeft: `1px solid ${t.hairline}`
                                }
                              }}
                            />
                          </TableRow>
                        )
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <StyleHelper.VerticalSpace />
            <Stack alignItems="center">
              <Typography
                sx={{
                  fontFamily: MONO,
                  fontSize: 11,
                  letterSpacing: '0.06em',
                  color: t.muted
                }}
              >
                Showing {visible.length} of {rows.length} · v{payload.version}
              </Typography>
            </Stack>
          </>
        )}
      </Container>
    </Box>
  )
}
